import React, { useState, useEffect, useMemo } from "react";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import JournalEntry from "./JournalEntry";
import Toast from "../Toast/Toast";
import { getUserTrades } from "../../utils/firebaseTradeUtils";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";

const Journal = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [trades, setTrades] = useState([]);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [userSettings, setUserSettings] = useState({
    useCommissions: false,
    commissionValue: 0,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState("day"); // day, week, month
  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      try {
        const [fetchedTrades, settingsSnap] = await Promise.all([
          getUserTrades(user.uid),
          getDoc(doc(db, "userSettings", user.uid)),
        ]);
        setUserSettings(settingsSnap.exists() ? settingsSnap.data() : {});
        setTrades(fetchedTrades);
      } catch (error) {
        console.error("Error fetching trades:", error);
        setError("Error loading trades");
      }
    };

    fetchData();
    window.addEventListener("tradesUpdated", fetchData);
    window.addEventListener("settingsUpdated", fetchData);
    return () => {
      window.removeEventListener("tradesUpdated", fetchData);
      window.removeEventListener("settingsUpdated", fetchData);
    };
  }, [user]);

  const getDateRange = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setHours(0, 0, 0, 0);

    switch (dateRange) {
      case "week": {
        const start = new Date(currentDate);
        start.setDate(currentDate.getDate() - currentDate.getDay());
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return { start, end };
      }
      case "month": {
        const start = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1
        );
        const end = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        );
        end.setHours(23, 59, 59, 999);
        return { start, end };
      }
      default: {
        const end = new Date(currentDate);
        end.setHours(23, 59, 59, 999);
        return { start: currentDate, end };
      }
    }
  };

  const filteredTrades = useMemo(() => {
    if (!trades) return [];
    const { start, end } = getDateRange();

    return trades
      .filter((trade) => {
        const tradeDate = new Date(trade.date);
        const matchesDate = tradeDate >= start && tradeDate <= end;
        const matchesSearch = searchQuery
          ? trade.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
            trade.setup?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            trade.tags?.some((tag) =>
              tag.text.toLowerCase().includes(searchQuery.toLowerCase())
            )
          : true;
        return matchesDate && matchesSearch;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [trades, selectedDate, dateRange, searchQuery]);

  const formatDate = () => {
    const { start, end } = getDateRange();
    const options = { year: "numeric", month: "long", day: "numeric" };

    if (dateRange === "day") {
      return start.toLocaleDateString("en-US", options);
    }
    return `${start.toLocaleDateString(
      "en-US",
      options
    )} - ${end.toLocaleDateString("en-US", options)}`;
  };

  const navigateDate = (direction) => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      switch (dateRange) {
        case "week":
          newDate.setDate(prev.getDate() + direction * 7);
          break;
        case "month":
          newDate.setMonth(prev.getMonth() + direction);
          break;
        default:
          newDate.setDate(prev.getDate() + direction);
      }
      return newDate;
    });
  };

  return (
    <div className="journal-container">
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          subText={toast.subText}
          onClose={() => setToast(null)}
        />
      )}

      <div className="journal-header">
        <button className="date-nav-button" onClick={() => navigateDate(-1)}>
          <ChevronLeft size={20} />
        </button>

        <div className="current-date">
          <Calendar size={20} />
          <h2>{formatDate()}</h2>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="date-range-select"
          >
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select>
        </div>

        <button className="date-nav-button" onClick={() => navigateDate(1)}>
          <ChevronRight size={20} />
        </button>

        <button
          className="filter-button"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={20} />
        </button>
      </div>

      {showFilters && (
        <div className="search-bar">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by symbol, setup, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}

      <div className="trade-entries">
        {error ? (
          <div className="error-message">{error}</div>
        ) : !trades ? (
          <div className="loading-container">
            <p>Loading trades...</p>
          </div>
        ) : filteredTrades.length > 0 ? (
          filteredTrades.map((trade) => (
            <JournalEntry
              key={trade.id}
              trade={trade}
              userId={user.uid}
              userSettings={userSettings}
              onError={(errorMsg) => {
                setToast({
                  type: "error",
                  message: "Error",
                  subText: errorMsg,
                });
              }}
            />
          ))
        ) : (
          <div className="no-trades-message">
            <p>No trades found for {formatDate()}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Journal;
